import { JsonLd, Seo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GLOSSARY_TERMS } from "../data/glossary";

// schema.org DefinedTermSet — surfaces every glossary entry to search engines
// as a structured term/definition pair. Computed once at module load (the term
// list is static), so it adds no per-render cost.
const GLOSSARY_JSONLD = {
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  name: "ETF Glossary — Corgi Funds",
  url: "https://corgifunds.com/glossary",
  hasDefinedTerm: GLOSSARY_TERMS.map((t) => ({
    "@type": "DefinedTerm",
    name: t.term,
    description: t.definition,
    ...(t.abbreviation ? { alternateName: t.abbreviation } : {}),
  })),
};

// GlossaryPage — "ETF Terms, Defined."
// Ported 1:1 from dane main (etf-website/glossary/page.tsx). Adaptations:
//   • SEO head/JSON-LD hooks dropped; GLOSSARY_TERMS imported directly
//   • interactive scroll-trigger anchors get a real `#fragment` href (with
//     preventDefault) so they stay valid/navigable under Biome a11y rules
//   • <button> filters get an explicit type="button"

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const ALL_CATEGORIES = [
  "All",
  "Basics",
  "Trading",
  "Income",
  "Risk",
  "Costs",
  "Structure",
  "Advanced",
];

function catClasses(cat: string): string {
  const key = cat.toLowerCase();
  const map: Record<string, string> = {
    basics: "border-[rgba(255,85,0,0.35)] text-[#ff9966]",
    trading: "border-[rgba(100,180,255,0.35)] text-[#80c0ff]",
    risk: "border-[rgba(255,79,79,0.35)] text-[#ff9090]",
    income: "border-[rgba(58,219,106,0.35)] text-[#7eeea0]",
    structure: "border-[rgba(180,130,255,0.35)] text-[#c8a0ff]",
    costs: "border-[rgba(255,200,0,0.35)] text-[#ffd966]",
    advanced: "border-[rgba(150,230,255,0.35)] text-[#a0e8ff]",
  };
  return map[key] || "";
}

function termToId(term: string): string {
  return `term-${term
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+$/g, "")}`;
}

export function GlossaryPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeLetter, setActiveLetter] = useState<string>("all");
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const entryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Group terms by first letter (matching static HTML placement)
  const groupedTerms = useMemo(() => {
    const groups: Record<string, typeof GLOSSARY_TERMS> = {};
    for (const term of GLOSSARY_TERMS) {
      // Special case: "30-Day SEC Yield" is placed under S in the static HTML
      let letter: string;
      if (term.term === "30-Day SEC Yield") {
        letter = "S";
      } else {
        const firstChar = term.term.charAt(0).toUpperCase();
        letter = /[A-Z]/.test(firstChar) ? firstChar : "#";
      }
      const bucket = groups[letter];
      if (bucket) {
        bucket.push(term);
      } else {
        groups[letter] = [term];
      }
    }
    // Preserve data-file order within each group (matches static HTML)
    return groups;
  }, []);

  // Letters that have terms
  const lettersWithTerms = useMemo(() => new Set(Object.keys(groupedTerms)), [groupedTerms]);

  // Filtered terms based on search, letter, and category
  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const result: Record<string, typeof GLOSSARY_TERMS> = {};

    for (const [letter, terms] of Object.entries(groupedTerms)) {
      // Letter filter
      if (activeLetter !== "all" && letter !== activeLetter) continue;

      const matching = terms.filter((t) => {
        // Category filter
        if (activeCategory !== "all") {
          const hasCat = t.categories.some((c) => c.toLowerCase() === activeCategory.toLowerCase());
          if (!hasCat) return false;
        }
        // Search filter
        if (q) {
          return (
            t.term.toLowerCase().includes(q) ||
            t.headline.toLowerCase().includes(q) ||
            t.definition.toLowerCase().includes(q) ||
            t.categories.some((c) => c.toLowerCase().includes(q))
          );
        }
        return true;
      });
      if (matching.length > 0) result[letter] = matching;
    }
    return result;
  }, [searchQuery, activeLetter, activeCategory, groupedTerms]);

  const sortedLetters = useMemo(
    () =>
      Object.keys(filteredGroups).sort((a, b) => {
        if (a === "#") return -1;
        if (b === "#") return 1;
        return a.localeCompare(b);
      }),
    [filteredGroups],
  );

  const filterLetter = useCallback((letter: string) => {
    setActiveLetter(letter);
    setActiveCategory("all");
    setSearchQuery("");
    if (letter !== "all") {
      const el = sectionRefs.current[letter];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, []);

  const filterCategory = useCallback((cat: string) => {
    setActiveCategory(cat);
    setActiveLetter("all");
    setSearchQuery("");
  }, []);

  const handleSearch = useCallback((val: string) => {
    setSearchQuery(val);
    // Don't reset filters when searching to allow combined filtering
  }, []);

  // Build sidebar data from filtered groups
  const sidebarData = useMemo(() => {
    return sortedLetters.map((letter) => ({
      letter,
      terms: (filteredGroups[letter] ?? []).map((t) => ({
        term: t.term,
        id: termToId(t.term),
      })),
    }));
  }, [sortedLetters, filteredGroups]);

  // On mount with a #term-... in the URL, scroll the matching entry into view
  // (browser's native fragment scroll fires before React mounts the entries).
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#term-")) return;
    const id = hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    });
  }, []);

  // Intersection observer for sidebar highlighting
  const [activeSidebarId, setActiveSidebarId] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: filteredGroups is the re-observe trigger — when the rendered term entries change we must re-attach the observer to the new nodes.
  useEffect(() => {
    const entries = Object.values(entryRefs.current).filter(Boolean) as HTMLDivElement[];
    if (entries.length === 0) return;

    const observer = new IntersectionObserver(
      (observedEntries) => {
        for (const entry of observedEntries) {
          if (entry.isIntersecting) {
            setActiveSidebarId(entry.target.id);
          }
        }
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );

    for (const el of entries) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, [filteredGroups]);

  const scrollToTerm = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSidebarId(id);
    }
  };

  return (
    <>
      <Seo
        title="ETF Glossary — Terms, Defined"
        description="Plain-English definitions of ETF terms — from expense ratios and NAV to buffers, leverage, and yield. The Corgi Funds glossary for investors."
        canonicalPath="/glossary"
      />
      <JsonLd data={GLOSSARY_JSONLD} />
      {/* Hero */}
      <section className="relative overflow-hidden grid grid-cols-2 gap-20 items-end border-b border-[var(--etf-border)] px-10 pt-[72px] pb-14 max-md:grid-cols-1 max-md:gap-8 max-md:px-5 max-md:py-8 before:content-['?'] before:absolute before:right-[-10px] before:bottom-[-60px] before:font-[family-name:var(--font-f37bolton)] before:text-[480px] before:leading-none before:text-[rgba(255,255,255,0.055)] before:pointer-events-none before:font-bold">
        <div>
          <div className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.2em] uppercase text-[var(--etf-orange)] mb-5 flex items-center gap-3 before:content-[''] before:inline-block before:w-6 before:h-px before:bg-[var(--etf-orange)]">
            ETF Glossary
          </div>
          <h1 className="font-[family-name:var(--font-f37bolton)] text-[clamp(52px,6.5vw,88px)] font-normal leading-[0.93] tracking-[0.01em] text-[var(--etf-white)]">
            ETF Terms,
            <br />
            <em className="not-italic text-[var(--etf-orange)]">Defined.</em>
          </h1>
        </div>
        <div className="flex flex-col gap-7 relative z-[1]">
          <p className="text-[15px] italic font-light text-[var(--etf-white2)] leading-[1.8] max-w-[480px]">
            A working reference for the terms that matter &mdash; from how ETFs are priced intraday
            to how yield is measured and reported. Plain language, no filler.
          </p>
          <p className="text-[13px] italic font-light text-[var(--etf-white2)] leading-[1.8] max-w-[480px] opacity-70 mt-2.5">
            Definitions are provided for educational purposes only. Any examples are illustrative
            and are not recommendations or investment advice.
          </p>
          <div className="flex gap-9">
            <div>
              <div className="font-[family-name:var(--font-f37bolton)] text-[36px] text-[var(--etf-white)]">
                {GLOSSARY_TERMS.length}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.1em] uppercase mt-0.5">
                Terms
              </div>
            </div>
            <div>
              <div className="font-[family-name:var(--font-f37bolton)] text-[36px] text-[var(--etf-white)]">
                {ALL_CATEGORIES.length - 1}
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.1em] uppercase mt-0.5">
                Categories
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls: alpha bar + category pills + search */}
      <div className="sticky top-14 z-[150] bg-[var(--etf-black2)] border-b border-[var(--etf-border)] flex items-center px-10 max-md:px-5">
        {/* Alphabetical jump-links */}
        <div className="flex items-center flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            type="button"
            onClick={() => filterLetter("all")}
            className={cn(
              "font-[family-name:var(--font-instrument)] text-[11px] font-medium tracking-[0.08em] text-[var(--etf-white3)] bg-none border-none px-3 py-4 cursor-pointer border-b-2 border-b-transparent transition-all duration-[0.14s] whitespace-nowrap uppercase",
              activeLetter === "all" && "text-[var(--etf-white)] !border-b-[var(--etf-orange)]",
            )}
          >
            All
          </button>
          {ALPHABET.map((letter) => {
            const hasTerms = lettersWithTerms.has(letter);
            return (
              <button
                key={letter}
                type="button"
                disabled={!hasTerms}
                onClick={() => hasTerms && filterLetter(letter)}
                className={cn(
                  "font-[family-name:var(--font-instrument)] text-[11px] font-medium tracking-[0.08em] text-[var(--etf-white3)] bg-none border-none px-3 py-4 cursor-pointer border-b-2 border-b-transparent transition-all duration-[0.14s] whitespace-nowrap uppercase",
                  "hover:not-[.active]:not-[.disabled]:text-[var(--etf-white2)]",
                  !hasTerms && "opacity-[0.22] cursor-default pointer-events-none",
                  activeLetter === letter &&
                    "text-[var(--etf-white)] !border-b-[var(--etf-orange)]",
                )}
              >
                {letter}
              </button>
            );
          })}
        </div>

        {/* Category pills */}
        <div className="flex gap-1.5 py-3 pl-5 border-l border-[var(--etf-border)] max-md:hidden">
          {ALL_CATEGORIES.map((cat) => {
            const catKey = cat === "All" ? "all" : cat.toLowerCase();
            const isActive = catKey === activeCategory;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => filterCategory(catKey)}
                className={cn(
                  "font-[family-name:var(--font-instrument)] text-[9px] tracking-[0.12em] uppercase px-[11px] py-[5px] border border-[var(--etf-border2)] bg-transparent text-[var(--etf-white3)] cursor-pointer transition-all duration-[0.14s] whitespace-nowrap",
                  !isActive && "hover:border-[var(--etf-white3)] hover:text-[var(--etf-white2)]",
                  isActive && "bg-[var(--etf-orange)] border-[var(--etf-orange)] text-white",
                )}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 border border-[var(--etf-border)] px-3.5 py-1.5 bg-[var(--etf-black3)] ml-3.5">
          <span className="text-[var(--etf-white3)] text-sm leading-none">&#x2315;</span>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="bg-none border-none outline-none font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white)] w-[150px] placeholder:text-[var(--etf-white3)]"
          />
        </div>
      </div>

      {/* Page body with sidebar */}
      <div className="grid grid-cols-[196px_1fr] min-h-[calc(100vh-112px)] max-md:grid-cols-1">
        {/* Sidebar */}
        <div className="sticky top-[112px] h-[calc(100vh-112px)] overflow-y-auto border-r border-[var(--etf-border2)] py-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-md:hidden">
          {sidebarData.map((group) => (
            <div key={group.letter} className="px-[22px] py-1 mt-1.5">
              <div className="font-[family-name:var(--font-instrument)] text-[9px] tracking-[0.2em] uppercase text-[var(--etf-orange)] mb-1">
                {group.letter}
              </div>
              {group.terms.map((t) => (
                <a
                  key={t.id}
                  href={`#${t.id}`}
                  className={cn(
                    "block font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] no-underline py-[3px] tracking-[0.02em] transition-colors duration-[0.12s] leading-[1.5] cursor-pointer",
                    "hover:text-[var(--etf-white2)]",
                    activeSidebarId === t.id && "!text-[var(--etf-orange)]",
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToTerm(t.id);
                  }}
                >
                  {t.term}
                </a>
              ))}
            </div>
          ))}
        </div>

        {/* Definitions content */}
        <div className="pb-20">
          {sortedLetters.length === 0 ? (
            <div className="py-20 px-12 text-center">
              <div className="font-[family-name:var(--font-f37bolton)] text-[72px] text-[var(--etf-white3)] opacity-[0.28]">
                ?
              </div>
              <div className="font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white3)] mt-2.5">
                No terms found matching your criteria.
              </div>
            </div>
          ) : (
            sortedLetters.map((letter) => (
              <div
                key={letter}
                ref={(el) => {
                  sectionRefs.current[letter] = el;
                }}
                className="border-b border-[var(--etf-border2)] last:border-b-0"
              >
                {/* Letter header */}
                <div className="flex items-center gap-4 border-b border-[var(--etf-border2)] bg-[var(--etf-black2)] sticky top-[112px] z-[5] px-12 pt-[18px] pb-3.5 max-md:px-5 max-md:pt-3 max-md:pb-2.5">
                  <span className="font-[family-name:var(--font-f37bolton)] text-[48px] leading-none text-[var(--etf-orange)] tracking-[0.02em]">
                    {letter}
                  </span>
                  <div className="flex-1 h-px bg-[var(--etf-border)]" />
                  <span className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.1em]">
                    {(filteredGroups[letter] ?? []).length}{" "}
                    {(filteredGroups[letter] ?? []).length === 1 ? "term" : "terms"}
                  </span>
                </div>

                {/* Terms */}
                {(filteredGroups[letter] ?? []).map((item, idx) => (
                  <div
                    key={item.term}
                    id={termToId(item.term)}
                    ref={(el) => {
                      entryRefs.current[termToId(item.term)] = el;
                    }}
                    className="group/entry grid grid-cols-[252px_1fr] gap-11 items-start border-b border-[var(--etf-border2)] last:border-b-0 relative transition-[background] duration-[0.15s] scroll-mt-[120px] animate-[glossFadeUp_0.35s_ease_both] px-12 py-7 hover:bg-[rgba(255,255,255,0.016)] before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-transparent before:transition-[background] before:duration-[0.15s] hover:before:bg-[var(--etf-orange)] max-md:grid-cols-1 max-md:gap-3 max-md:px-5 max-md:py-5"
                    style={{ animationDelay: `${idx * 0.025}s` }}
                  >
                    {/* Term meta (left column) */}
                    <div className="min-w-0">
                      <div className="font-[family-name:var(--font-f37bolton)] text-2xl tracking-[0.04em] text-[var(--etf-white)] leading-[1.05] mb-[7px]">
                        {item.term}
                      </div>
                      {item.abbreviation && (
                        <div className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-orange)] tracking-[0.18em] uppercase mb-[9px]">
                          {item.abbreviation}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-[5px] mb-2.5">
                        {item.categories.map((cat) => (
                          <span
                            key={cat}
                            className={cn(
                              "font-[family-name:var(--font-instrument)] text-[8px] tracking-[0.12em] uppercase px-2 py-[3px] border",
                              catClasses(cat),
                            )}
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                      <div className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)] tracking-[0.14em] opacity-[0.38] mt-2">
                        {item.index}
                      </div>
                    </div>

                    {/* Definition body (right column) */}
                    <div className="min-w-0">
                      <div className="text-base font-medium text-[var(--etf-white)] leading-[1.4] mb-2.5 italic">
                        {item.headline}
                      </div>
                      <div className="text-[13.5px] font-light text-[var(--etf-white2)] leading-[1.9] mb-3 [&_strong]:font-medium [&_strong]:text-[var(--etf-white)]">
                        {item.definition}
                      </div>
                      {item.formula && (
                        <div className="bg-[var(--etf-black3)] border border-[var(--etf-border2)] border-l-[3px] border-l-[var(--etf-orange)] px-4 py-3 my-3 font-[family-name:var(--font-instrument)] text-[11px] text-[var(--etf-white2)] tracking-[0.02em] leading-[1.7]">
                          {item.formulaLabel && (
                            <div className="text-[9px] tracking-[0.16em] uppercase text-[var(--etf-orange)] mb-[5px]">
                              {item.formulaLabel}
                            </div>
                          )}
                          {item.formula}
                        </div>
                      )}
                      {item.related && item.related.length > 0 && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)] tracking-[0.14em] uppercase shrink-0">
                            See also
                          </span>
                          {item.related.map((r, i, related) => (
                            <a
                              key={r.id}
                              href={`#${r.id}`}
                              className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-orange)] no-underline opacity-70 transition-opacity duration-[0.14s] tracking-[0.04em] cursor-pointer hover:opacity-100"
                              onClick={(e) => {
                                e.preventDefault();
                                scrollToTerm(r.id);
                              }}
                            >
                              {r.label}
                              {i < related.length - 1 && (
                                <span className="text-[var(--etf-white3)] ml-2">&middot;</span>
                              )}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
